import { html, nothing } from "lit";
import { formatDurationMs } from "../format.ts";
import type { BibleFinalSummary, FleetWorkPlanRecord } from "../types.ts";
import {
  collectPlanChangedFiles,
  collectPlanEvidence,
  type AggregatedFile,
} from "./fleet-plan-agent-cards.ts";
import { fleetStatusPillClass } from "./fleet-plan-labels.ts";

export type BibleSummaryView = {
  done: string[];
  notDone: string[];
  why: string[];
  changed: string[];
  nextSteps: string[];
};

const FINAL_PLAN_STATUSES = new Set(["completed", "failed", "abandoned", "stopped"]);

export function bibleSummaryIsFinal(params: {
  planStatus?: string;
  requiresDecision?: boolean;
}): boolean {
  if (params.requiresDecision) {
    return true;
  }
  return FINAL_PLAN_STATUSES.has(params.planStatus ?? "");
}

function formatDiffStat(file: AggregatedFile): string {
  if (!file.added && !file.removed) {
    return "";
  }
  return `+${file.added} −${file.removed}`;
}

function evidenceKindLabel(kind: string): string {
  switch (kind) {
    case "unit_tests":
      return "Unit tests";
    case "type_check":
      return "Type check";
    case "lint_check":
      return "Lint";
    case "api_endpoints":
      return "API endpoints";
    case "integration_tests":
      return "Integration tests";
    default:
      return kind;
  }
}

export function renderBibleFinalSummaryCard(
  summary: BibleSummaryView | BibleFinalSummary | null | undefined,
  opts?: {
    planStatus?: string;
    requiresDecision?: boolean;
    plan?: FleetWorkPlanRecord | null;
  },
) {
  const plan = opts?.plan ?? null;
  const files = plan ? collectPlanChangedFiles(plan) : [];
  const evidence = plan ? collectPlanEvidence(plan) : [];
  const implemented = summary?.done ?? [];
  const outstanding = [
    ...(summary?.notDone ?? []),
    ...(summary?.why ?? []).filter((line) => !(summary?.notDone ?? []).includes(line)),
  ];
  const nextSteps = summary?.nextSteps ?? [];
  const changedFallback = summary?.changed ?? [];
  const hasContent =
    implemented.length +
      outstanding.length +
      nextSteps.length +
      changedFallback.length +
      files.length +
      evidence.length >
    0;
  if (!hasContent) {
    return nothing;
  }
  const isFinal = bibleSummaryIsFinal(opts ?? {});
  const planId = plan?.id;
  const title = planId
    ? `Done for plan ${planId.slice(0, 8)}:`
    : isFinal
      ? "Final summary"
      : "Plan summary";

  return html`
    <div class="work-plan-summary" data-testid="work-plan-summary">
      <div class="work-plan-summary__head">
        <div class="work-plan-summary__title">${title}</div>
        ${
          opts?.planStatus
            ? html`<span class=${fleetStatusPillClass(opts.planStatus)}>${opts.planStatus}</span>`
            : nothing
        }
      </div>
      <div class="work-plan-summary__grid">
        <div class="work-plan-summary__col">
          ${
            implemented.length > 0
              ? html`
                  <div class="work-plan-summary__section">
                    <div class="field-label">Implemented</div>
                    <ul>
                      ${implemented.map((line) => html`<li>${line}</li>`)}
                    </ul>
                  </div>
                `
              : nothing
          }
          ${
            files.length > 0
              ? html`
                  <div class="work-plan-summary__section">
                    <div class="field-label">Changed files (${files.length})</div>
                    <ul class="work-plan-summary__files">
                      ${files.map(
                        (file) => html`
                          <li>
                            <span class="mono">${file.path}</span>
                            <span class="fleet-agent-card__file-kind" data-kind=${file.changeKind}
                              >${file.changeKind}</span
                            >
                            <span class="fleet-agent-card__diff-stat">${formatDiffStat(file)}</span>
                          </li>
                        `,
                      )}
                    </ul>
                  </div>
                `
              : changedFallback.length > 0
                ? html`
                    <div class="work-plan-summary__section">
                      <div class="field-label">Changed files</div>
                      <ul>
                        ${changedFallback.map((line) => html`<li>${line}</li>`)}
                      </ul>
                    </div>
                  `
                : nothing
          }
        </div>
        <div class="work-plan-summary__col">
          ${
            evidence.length > 0
              ? html`
                  <div class="work-plan-summary__section">
                    <div class="field-label">Validation evidence</div>
                    <ul class="work-plan-summary__evidence">
                      ${evidence.map(
                        (e) => html`
                          <li>
                            <span>${evidenceKindLabel(e.kind)}</span>
                            <span class="muted">${e.detail ?? e.status}</span>
                            ${
                              e.durationMs
                                ? html`<span class="muted mono"
                                    >${formatDurationMs(e.durationMs)}</span
                                  >`
                                : nothing
                            }
                          </li>
                        `,
                      )}
                    </ul>
                  </div>
                `
              : nothing
          }
          ${
            outstanding.length > 0
              ? html`
                  <div class="work-plan-summary__section">
                    <div class="field-label">Outstanding notes</div>
                    <ul>
                      ${outstanding.map((line) => html`<li>${line}</li>`)}
                    </ul>
                  </div>
                `
              : nothing
          }
          ${
            nextSteps.length > 0
              ? html`
                  <div class="work-plan-summary__section">
                    <div class="field-label">Next steps</div>
                    <ul>
                      ${nextSteps.map((line) => html`<li>${line}</li>`)}
                    </ul>
                  </div>
                `
              : nothing
          }
        </div>
      </div>
    </div>
  `;
}
